import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

const ACTIONS = ['all', 'create', 'update', 'publish', 'unpublish', 'schedule', 'enable', 'disable', 'delete'];
const ENTITIES = ['all', 'article', 'category', 'tag'];

const actionColor: Record<string, string> = {
  create: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  update: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  publish: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  unpublish: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  schedule: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300',
  delete: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  enable: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  disable: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
};

export default function NewsAuditLog() {
  const [entity, setEntity] = useState('all');
  const [action, setAction] = useState('all');
  const [search, setSearch] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['news-audit-log', entity, action],
    queryFn: async () => {
      let q = supabase.from('news_audit_log').select('*').order('created_at', { ascending: false }).limit(500);
      if (entity !== 'all') q = q.eq('entity_type', entity);
      if (action !== 'all') q = q.eq('action', action);
      const { data } = await q;
      return data || [];
    },
  });

  const filtered = rows.filter((r: any) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (r.summary || '').toLowerCase().includes(s) || (r.actor_email || '').toLowerCase().includes(s);
  });

  return (
    <div className="container max-w-6xl py-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold">News audit log</h1>
          <p className="text-sm text-muted-foreground">Every news article, category, and tag change with actor and timestamp.</p>
        </div>
        <Button variant="outline" asChild><Link to="/dashboard/news">Back to articles</Link></Button>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center gap-3 flex-wrap">
          <CardTitle className="text-base mr-auto">Recent events</CardTitle>
          <Input placeholder="Search title or actor…" className="max-w-xs" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{ENTITIES.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <th className="p-3">When</th>
                <th className="p-3">Action</th>
                <th className="p-3">Entity</th>
                <th className="p-3">Item</th>
                <th className="p-3">Actor</th>
                <th className="p-3">Changes</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
              {!isLoading && filtered.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No entries.</td></tr>}
              {filtered.map((r: any) => (
                <tr key={r.id} className="border-t align-top">
                  <td className="p-3 whitespace-nowrap text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3"><Badge className={actionColor[r.action] || ''} variant="secondary">{r.action}</Badge></td>
                  <td className="p-3 text-xs">{r.entity_type}</td>
                  <td className="p-3">
                    {r.entity_type === 'article' && r.entity_id ? (
                      <Link to={`/dashboard/news/${r.entity_id}/edit`} className="font-medium hover:text-primary">{r.summary || '(untitled)'}</Link>
                    ) : (
                      <span className="font-medium">{r.summary || '—'}</span>
                    )}
                  </td>
                  <td className="p-3 text-xs">{r.actor_email || (r.actor_id ? r.actor_id.slice(0, 8) : '—')}</td>
                  <td className="p-3 text-xs">
                    {r.diff && Object.keys(r.diff).length > 0 ? (
                      <details>
                        <summary className="cursor-pointer text-primary">View diff</summary>
                        <pre className="mt-2 p-2 bg-muted rounded text-[11px] overflow-x-auto max-w-md">
{JSON.stringify(r.diff, null, 2)}
                        </pre>
                      </details>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
